/**
 * Admin V3 — Pricing — Schedule A diff utility
 *
 * Pure function: computes a field-level diff between the current schedule_a
 * (as loaded from GET /api/pricing/config) and a proposed schedule_a
 * (the operator's in-progress edits).
 *
 * Scope discipline (matches owner constraints):
 *   - Diff covers ONLY editable fields: bands[*], class_multipliers (standard/
 *     corporate/partner), fixed_routes[*].{fixed_price, bidirectional,
 *     service_types, pickup_keywords, dropoff_keywords, name}.
 *   - Diff EXCLUDES read-only fields: legacy config (any of the 7 fields),
 *     class_multipliers.business, class_multipliers.van, and any
 *     class_multipliers.priority entry (which must not exist anyway).
 *   - Returns a flat array of {path, before, after} entries for display in
 *     the confirmation modal.
 *
 * Note: float equality uses an epsilon to avoid spurious diffs from
 * floating-point representation drift (e.g., 2.20 vs 2.2 are EQUAL).
 */

const EPSILON = 1e-9;

const eqNumber = (a, b) => {
  if (typeof a !== 'number' || typeof b !== 'number') return a === b;
  return Math.abs(a - b) < EPSILON;
};

const eqArrayShallow = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return a === b;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

const eqValue = (a, b) => {
  if (typeof a === 'number' || typeof b === 'number') return eqNumber(a, b);
  if (Array.isArray(a) || Array.isArray(b)) return eqArrayShallow(a, b);
  return a === b;
};

const diffBands = (current, proposed, out) => {
  const c = current || [];
  const p = proposed || [];
  const len = Math.max(c.length, p.length);
  for (let i = 0; i < len; i += 1) {
    const cb = c[i] || {};
    const pb = p[i] || {};
    ['upper_km', 'mode', 'flat_fare', 'additive_base', 'additive_rate_per_km', 'additive_lower_km'].forEach((k) => {
      if (!eqValue(cb[k], pb[k])) {
        out.push({ path: `bands[${i}].${k}`, before: cb[k], after: pb[k] });
      }
    });
  }
};

const diffClassMultipliers = (current, proposed, out) => {
  const c = current || {};
  const p = proposed || {};
  // ONLY supported, editable keys; business/van/priority are excluded from diff.
  ['standard', 'corporate', 'partner'].forEach((k) => {
    if (!eqValue(c[k], p[k])) {
      out.push({ path: `class_multipliers.${k}`, before: c[k], after: p[k] });
    }
  });
};

const diffFixedRoutes = (current, proposed, out) => {
  const c = current || [];
  const p = proposed || [];
  const len = Math.max(c.length, p.length);
  for (let i = 0; i < len; i += 1) {
    const cr = c[i] || {};
    const pr = p[i] || {};
    // Phase 1 scope: only in-place edits on existing routes' editable fields.
    ['name', 'fixed_price', 'bidirectional'].forEach((k) => {
      if (!eqValue(cr[k], pr[k])) {
        out.push({ path: `fixed_routes[${i}].${k}`, before: cr[k], after: pr[k] });
      }
    });
    ['pickup_keywords', 'dropoff_keywords', 'service_types'].forEach((k) => {
      if (!eqArrayShallow(cr[k], pr[k])) {
        out.push({
          path: `fixed_routes[${i}].${k}`,
          before: Array.isArray(cr[k]) ? cr[k].join(', ') : '—',
          after:  Array.isArray(pr[k]) ? pr[k].join(', ') : '—',
        });
      }
    });
  }
};

/**
 * @param {Object} current  - current schedule_a (from GET response)
 * @param {Object} proposed - proposed schedule_a (operator edits)
 * @returns {Array<{path: string, before: any, after: any}>}
 */
export const diffScheduleA = (current, proposed) => {
  const out = [];
  if (!current && !proposed) return out;
  diffBands(current?.bands, proposed?.bands, out);
  diffClassMultipliers(current?.class_multipliers, proposed?.class_multipliers, out);
  diffFixedRoutes(current?.fixed_routes, proposed?.fixed_routes, out);
  return out;
};

export default diffScheduleA;
